import { useRef, useCallback } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { 
  User, 
  Mail, 
  Phone, 
  Ticket, 
  Calendar,
  CreditCard,
  Hash,
  Download
} from "lucide-react";
import { formatCurrency } from "@/lib/currencyUtils";
import { format } from "date-fns";
import { QRCodeCanvas } from "qrcode.react";

interface Order {
  id: string;
  reference: string;
  user_first_name: string;
  user_last_name: string;
  user_email: string;
  phone_number_complete: string | null;
  total_amount: number;
  status: string;
  created_at: string;
  vip_ticket_quantity: number | null;
  standing_tickets: number | null;
  early_bird_tickets: number | null;
  vip_seats: string[] | null;
  promo_code: string | null;
  marketing_consent: boolean;
  terms_accepted: boolean;
}

interface OrderDetailsSheetProps {
  order: Order | null;
  eventTitle: string;
  currency: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const OrderDetailsSheet = ({
  order,
  eventTitle,
  currency,
  open,
  onOpenChange,
}: OrderDetailsSheetProps) => {
  const qrRef = useRef<HTMLDivElement>(null);

  const handleDownloadQR = useCallback(() => {
    if (!order || !qrRef.current) return;
    
    const canvas = qrRef.current.querySelector('canvas');
    if (!canvas) return;

    const link = document.createElement('a');
    link.download = `ticket-${order.reference}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  }, [order]);

  if (!order) return null;

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
      case 'paid':
        return 'bg-green-500/10 text-green-500 border-green-500/20';
      case 'pending':
        return 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20';
      case 'refunded':
        return 'bg-orange-500/10 text-orange-500 border-orange-500/20';
      case 'cancelled':
      case 'failed':
        return 'bg-destructive/10 text-destructive border-destructive/20';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const ticketBreakdown = [];
  if (order.vip_ticket_quantity && order.vip_ticket_quantity > 0) {
    ticketBreakdown.push({ type: 'VIP', quantity: order.vip_ticket_quantity });
  }
  if (order.early_bird_tickets && order.early_bird_tickets > 0) {
    ticketBreakdown.push({ type: 'Early Bird', quantity: order.early_bird_tickets });
  }
  if (order.standing_tickets && order.standing_tickets > 0) {
    ticketBreakdown.push({ type: 'General Admission', quantity: order.standing_tickets });
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader className="text-left">
          <div className="flex items-center justify-between">
            <SheetTitle className="text-xl">Order Details</SheetTitle>
            <Badge 
              variant="outline" 
              className={getStatusColor(order.status)}
            >
              {order.status}
            </Badge>
          </div>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {/* Order Reference */}
          <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
            <Hash className="w-5 h-5 text-primary" />
            <div>
              <p className="text-xs text-muted-foreground">Reference</p>
              <p className="font-mono font-semibold">{order.reference}</p>
            </div>
          </div>

          {/* QR Code for Entry */}
          <div className="flex flex-col items-center gap-3 p-4 bg-muted/30 rounded-lg border border-border/50">
            <p className="text-sm font-medium text-muted-foreground">Entry QR Code</p>
            <div ref={qrRef} className="bg-white p-3 rounded-lg">
              <QRCodeCanvas
                value={order.reference}
                size={160}
                level="H"
                includeMargin={false}
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadQR}
              className="gap-2"
            >
              <Download className="w-4 h-4" />
              Download QR Code
            </Button>
          </div>

          {/* Event */}
          <div>
            <h4 className="text-sm font-medium text-muted-foreground mb-2">Event</h4>
            <p className="font-medium">{eventTitle}</p>
          </div>

          <Separator />

          {/* Customer Info */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-muted-foreground">Customer Information</h4>
            
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <User className="w-4 h-4 text-muted-foreground" />
                <span>{order.user_first_name} {order.user_last_name}</span>
              </div>
              
              <div className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-muted-foreground" />
                <a 
                  href={`mailto:${order.user_email}`}
                  className="text-primary hover:underline break-all"
                >
                  {order.user_email}
                </a>
              </div>
              
              {order.phone_number_complete && (
                <div className="flex items-center gap-3">
                  <Phone className="w-4 h-4 text-muted-foreground" />
                  <a 
                    href={`tel:${order.phone_number_complete}`}
                    className="text-primary hover:underline"
                  >
                    {order.phone_number_complete}
                  </a>
                </div>
              )}
            </div>
          </div>

          <Separator />

          {/* Tickets */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-muted-foreground">Tickets</h4>
            
            <div className="space-y-2">
              {ticketBreakdown.map((item, idx) => (
                <div 
                  key={idx}
                  className="flex items-center justify-between p-3 bg-muted/30 rounded-lg"
                >
                  <div className="flex items-center gap-2">
                    <Ticket className="w-4 h-4 text-primary" />
                    <span>{item.type}</span>
                  </div>
                  <span className="font-medium">x{item.quantity}</span>
                </div>
              ))}
            </div>

            {order.vip_seats && order.vip_seats.length > 0 && (
              <div className="mt-3">
                <p className="text-sm text-muted-foreground mb-2">VIP Seats</p>
                <div className="flex flex-wrap gap-2">
                  {order.vip_seats.map((seat, idx) => (
                    <Badge key={idx} variant="secondary">
                      {seat}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>

          <Separator />

          {/* Payment */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-muted-foreground">Payment</h4>
            
            <div className="flex items-center justify-between p-4 bg-primary/5 rounded-lg border border-primary/10">
              <div className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-primary" />
                <span className="text-muted-foreground">Total Paid</span>
              </div>
              <span className="text-xl font-bold text-primary">
                {formatCurrency(order.total_amount, currency)}
              </span>
            </div>

            {order.promo_code && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Promo Code</span>
                <Badge variant="outline">{order.promo_code}</Badge>
              </div>
            )}
          </div>

          <Separator />

          {/* Order Date */}
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            <Calendar className="w-4 h-4" />
            <span>
              Ordered {format(new Date(order.created_at), 'PPP p')}
            </span>
          </div>

          {/* Consents */}
          <div className="space-y-2 text-xs text-muted-foreground">
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${order.marketing_consent ? 'bg-green-500' : 'bg-muted'}`} />
              <span>Marketing consent: {order.marketing_consent ? 'Yes' : 'No'}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${order.terms_accepted ? 'bg-green-500' : 'bg-muted'}`} />
              <span>Terms accepted: {order.terms_accepted ? 'Yes' : 'No'}</span>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
